import React, { createContext, useMemo } from 'react'

import { serverSideQuery, useQuery } from '@/api'
import { URGENCY_VELOCITY_ENDPOINT } from '@/constants'

import { urgencyLongtermSelector, velocityLongtermSelector } from './selectors'
import type {
    VelocityContextType,
    UrgencyVelocityProviderProps,
    UrgencyVelocityResponse,
    UrgencyVelocitySelectorsType,
    UrgencyVelocityStateType,
    UrgencyVelocityResource,
} from './types'

const defaultContextValue: VelocityContextType = {
    state: {},
    selectors: {
        urgencyLongterm: undefined,
        velocityLongterm: undefined,
    },
    fetching: true,
}

const UrgencyVelocityContext = createContext<VelocityContextType>(defaultContextValue)
const { Provider } = UrgencyVelocityContext

export const UrgencyVelocityProvider: React.FC<UrgencyVelocityProviderProps> = ({
    children,
    initialParams,
    initialProps,
}) => {
    const { fetching, data, error } = useQuery<UrgencyVelocityResponse>({
        endpoint: URGENCY_VELOCITY_ENDPOINT,
        params: initialParams,
        initialData: initialProps.data,
        initialFetchDisabled: !!initialProps.data,
        enabled: !!initialParams.resource && (!!initialParams.resourceId || initialParams.resourceId === 0),
    })

    const contextValue: VelocityContextType = useMemo(() => {
        const state: UrgencyVelocityStateType = { fetching, data, error }
        const selectors: UrgencyVelocitySelectorsType = {
            urgencyLongterm: urgencyLongtermSelector(state),
            velocityLongterm: velocityLongtermSelector(state),
        }

        return {
            state,
            selectors,
            fetching,
        }
    }, [fetching, data, error])

    return <Provider value={contextValue}>{children}</Provider>
}

export default UrgencyVelocityContext

export const serverSideRead = async ({
    resource,
    resourceId,
}: {
    resource: UrgencyVelocityResource
    resourceId: number
}): Promise<UrgencyVelocityResponse> => {
    const queryInstance = serverSideQuery<UrgencyVelocityResponse>(URGENCY_VELOCITY_ENDPOINT)
    const params = { resource, resourceId }
    const urgencyVelocityData = await queryInstance(params)
    return urgencyVelocityData
}
